import { MaterialService } from './material.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ExaminationSession, ExaminationSessionList } from '../model/examination-session.model';
import { CommunicationToken } from '../model/communication-token.model';

@Injectable()
export class ExaminationSessionService extends KiwixiService {

    api = environment.server + 'examinations_sessions/';

    constructor(
        private _http: HttpClient,
        private _router: Router,
        kiwixiGlobals: KiwixiGlobals,
        private materialService: MaterialService,) {
        super(_http, _router, kiwixiGlobals, materialService);
    }

    getExaminationSessionById(_id): Observable<ExaminationSession> {
        const url = this.api + _id + '/';
        return this.getExaminationSessionByUrl(url);
    }

    getExaminationSessionByUrl(_url): Observable<ExaminationSession> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationSession>(_url, {
            headers: header
        })
            .pipe(
                catchError(this.handleErrorObservable<any>(`getExaminationSession`))
            );
    }

    getExaminationSessions(params: HttpParams): Observable<ExaminationSessionList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationSessionList>(this.api, {
            headers: header,
            params: params
        })
            .pipe(
                map(_ExaminationSessions => _ExaminationSessions),
                catchError(this.handleErrorObservable<ExaminationSessionList>(`getObservableExaminationSessions`))
            );
    }

    getExaminationSessionsByUrl(_url): Observable<ExaminationSessionList> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationSessionList>(_url, {
            headers: header,
        })
            .pipe(
                map(_ExaminationSessions => _ExaminationSessions),
                catchError(this.handleErrorObservable<ExaminationSessionList>(`getObservableExaminationSessions`))
            );
    }

    patchExaminationSession(_id, params) {
        const url = this.api + _id + '/';
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.patch<ExaminationSession>(url, params, { headers: headers })
            .pipe(
                catchError(this.handleErrorObservable<ExaminationSession>(`Création d'une fiche`)),
            );
    }

    getOrCreateExaminationByLilateId(_id): Observable<ExaminationSession> {
        const url = environment.server + 'examinations/get_or_create_sessions?lilate_id=' + _id + '&salt=' + Math.random();
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<ExaminationSession>(url, {
            headers: header
        })
            .pipe(
                catchError(this.handleErrorObservable<any>(`getExaminationSession`))
            );
    }

    interruptExaminationSession(_id: number, params: any): Observable<any> {
        const url = this.api + _id.toString() + '/interrupt_lilate/';
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.post<any>(url, params, { headers: headers })
            .pipe(
                catchError(this.handleErrorObservable<any>(`interruptExaminationSession`))
            );
    }

    /**
     * Jeton de visioconférence (Azure Communication Services) d'une session d'examen.
     * POST /examinations_sessions/<id>/communication_token/
     *
     * Délivré par Engage seulement au candidat, à l'examinateur de la session et au staff ;
     * la clé d'accès Azure ne quitte plus le serveur. Pas de `handleErrorObservable` ici :
     * l'appelant gère l'échec (503 : visioconférence non configurée, 429 : trop de demandes).
     */
    getCommunicationToken(_id: number): Observable<CommunicationToken> {
        const url = this.api + _id.toString() + '/communication_token/';
        const headers = this.createHttpClientAuthorizationHeader();
        return this._http.post<CommunicationToken>(url, {}, { headers: headers });
    }

    getExaminationSessionsStatus() {
        return [
            {
                value: 'pending',
                label: 'En attente',
                valid: true
            },
            {
                value: 'ongoing',
                label: 'En cours',
                valid: true
            },
            {
                value: 'pending_assessment',
                label: 'Evaluation en cours',
                valid: true
            },
            {
                value: 'done',
                label: 'Terminée',
                valid: true
            },
            {
                value: 'cancelled',
                label: 'Annulée',
                valid: true
            },
            {
                value: 'abs_teacher_without_justification',
                label: 'Examinateur absent',
                valid: true
            },
            {
                value: 'abs_student_without_justification',
                label: 'Candidat absent',
                valid: true
            },
            {
                value: 'cancelled_for_technical_reason_teacher',
                label: 'Annulée pour raison technique examinateur',
                valid: true
            },
            {
                value: 'cancelled_for_technical_reason_student',
                label: 'Annulée pour raison technique candidat',
                valid: true
            },
            {
                value: 'cancelled_for_technical_reason_lingueo',
                label: 'Annulée pour raison technique Lingueo',
                valid: true
            }
        ];
    }
}
